import React, { useState, useEffect, Suspense } from 'react';
import './App.css';
import { AuthProvider, useAuth } from './components/AuthContext';
import Home from './components/Home';
import { IN_TESTING } from './config/flags';

// Clinical & Psychometric Tools
const ArchetypeApp = React.lazy(() => import('./components/ArchetypeApp'));
const DiscApp = React.lazy(() => import('./components/DiscApp'));
const FiveFactorApp = React.lazy(() => import('./components/FiveFactorApp'));
const Dass21App = React.lazy(() => import('./components/Dass21App'));
const Obi16App = React.lazy(() => import('./components/Obi16App'));
const Uwes17App = React.lazy(() => import('./components/Uwes17App'));
const Sds6App = React.lazy(() => import('./components/Sds6App'));
const CopsoqTool = React.lazy(() => import('./components/CopsoqTool'));
const SapphireApp = React.lazy(() => import('./components/SapphireApp'));
const WorkingGeniusApp = React.lazy(() => import('./components/WorkingGeniusApp'));

// Reflective Practice Tools
const TargetApp = React.lazy(() => import('./components/TargetApp'));
const TrafficApp = React.lazy(() => import('./components/TrafficApp'));
const CompassApp = React.lazy(() => import('./components/CompassApp'));
const CecApp = React.lazy(() => import('./components/CecApp'));
const ComplexityApp = React.lazy(() => import('./components/ComplexityApp'));
const ThreeYsApp = React.lazy(() => import('./components/ThreeYsApp'));
const StepInsideApp = React.lazy(() => import('./components/StepInsideApp'));
const MoreThanWordsApp = React.lazy(() => import('./components/MoreThanWordsApp'));
const FocusCardsApp = React.lazy(() => import('./components/FocusCardsApp'));
const BridgeApp = React.lazy(() => import('./components/BridgeApp'));
const StickingPointsApp = React.lazy(() => import('./components/StickingPointsApp'));
const CircleOfViewpointsApp = React.lazy(() => import('./components/CircleOfViewpointsApp'));
const UsedToThinkApp = React.lazy(() => import('./components/UsedToThinkApp'));
const ClaimSupportApp = React.lazy(() => import('./components/ClaimSupportApp'));
const TugOfWarApp = React.lazy(() => import('./components/TugOfWarApp'));
const TrueForWhoApp = React.lazy(() => import('./components/TrueForWhoApp'));
const HeadlinesApp = React.lazy(() => import('./components/HeadlinesApp'));
const GenerateSortApp = React.lazy(() => import('./components/GenerateSortApp'));
const EnergyAuditApp = React.lazy(() => import('./components/EnergyAuditApp'));
const FourBatteriesApp = React.lazy(() => import('./components/FourBatteriesApp'));
const BandwidthGaugeApp = React.lazy(() => import('./components/BandwidthGaugeApp'));
const ConnectedApp = React.lazy(() => import('./components/ConnectedApp'));
const DecisionCircleApp = React.lazy(() => import('./components/DecisionCircleApp'));


// Modals
import TriageModal from './components/TriageModal';
import SupervisionTriageModal from './components/SupervisionTriageModal';
import AdminApp from './components/AdminApp';

// ---------------------------------------------------------
// LOADER FALLBACK FOR LAZY TOOL CHUNKS
// ---------------------------------------------------------
const ToolLoader = ({ customText, subtext }) => {
    const [messageIndex, setMessageIndex] = useState(0);
    const messages = [
        "Allocating memory workspace...",
        "Decrypting relational matrices...",
        "Waking up AI rendering engine...",
        "Establishing secure context...",
        "Finalizing UI geometry..."
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setMessageIndex((prev) => (prev + 1) % messages.length);
        }, 1200);
        return () => clearInterval(interval);
    }, [messages.length]);

    return (
        <div className="tool-loader-overlay">
            <div className="tool-loader-ring"></div>
            <div className="tool-loader-text">{customText || "Loading Module"}</div>
            <div className="tool-loader-subtext">{subtext || messages[messageIndex]}</div>
        </div>
    );
};

// ---------------------------------------------------------
// FLOATING PRO UPGRADE BUTTON
// ---------------------------------------------------------
const FloatingProUpgradeButton = ({ currentTool }) => {
    const { isPremium, user } = useAuth();

    // 🛡️ Suppress button if user is premium, inside a tool, or in testing mode
    if (isPremium || currentTool || IN_TESTING) return null;

    return (
        <div style={{ position: 'fixed', top: '0', right: user ? '240px' : '110px', height: '90px', display: 'flex', alignItems: 'center', zIndex: 1001 }} className="hide-on-mobile">
            <button 
                onClick={() => window.location.href = '/?triage=closed&view=pricing'}
                style={{ background: 'var(--card-bg)', color: 'var(--text-main)', border: '1px solid var(--border-subtle)', borderRadius: '20px', padding: '8px 16px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 6px var(--card-shadow)' }}
            >
                Buy Pro License (from $2/mo)
            </button>
        </div>
    );
};

// ---------------------------------------------------------
// GLOBAL CONTEXTUAL PORTAL ACTIONS
// ---------------------------------------------------------
const FloatingToolActionButton = ({ currentTool }) => {
    const { isPremium } = useAuth();

    const supervisionTools = ['target', 'traffic', 'compass', 'cec', 'complexity', '3ys', 'stepinside', 'morethanwords', 'focuscards', 'bridge', 'stickingpoints', 'circleofviewpoints', 'usedtothink', 'claimsupport', 'tugofwar', 'trueforwho', 'headlines', 'generatesort', 'energyaudit', 'fourbatteries', 'bandwidthgauge', 'connected', 'decisioncircle'];
    const clinicalTools = ['archetype', 'disc', 'fivefactor', 'dass21', 'obi16', 'uwes17', 'copsoq', 'sds6', 'sapphire'];

    if (isPremium || !currentTool) return null;

    const isSupervision = supervisionTools.includes(currentTool);
    const isClinical = clinicalTools.includes(currentTool);

    if (!isSupervision && !isClinical) return null;

    const handlePortalRedirect = () => {
        if (!window.Calendly) {
            window.open('https://calendly.com/eric-cheung-professionalsupervision/regular-supervision-online', '_blank');
            return;
        }

        if (isSupervision || isClinical) {
            window.Calendly.initPopupWidget({ url: 'https://calendly.com/eric-cheung-professionalsupervision/regular-supervision-online' });
        }
    };

    return (
        <button 
            className={`supervisor-shortcut-btn animate-fade ${isClinical ? 'clinical-mode' : ''}`}
            onClick={handlePortalRedirect}
            title="Speak to a Supervisor"
        >
            <div>Speak to a</div>
            <div>Supervisor</div>
        </button>
    );
};

// ---------------------------------------------------------
// INNER APP CONTENT
// ---------------------------------------------------------
function AppContent() {
    const { loadingAuth } = useAuth();
    const params = new URLSearchParams(window.location.search);
    const tool = params.get('tool');
    const triageState = params.get('triage');

    // 🚀 REMOVE HTML PRELOADER ONLY WHEN APP IS 100% READY
    useEffect(() => {
        if (!loadingAuth) {
            const preloader = document.getElementById('app-preloader');
            if (preloader) {
                preloader.style.opacity = '0';
                setTimeout(() => {
                    preloader.remove();
                }, 300);
            }
        }
    }, [loadingAuth]);

    if (loadingAuth) {
        // Return null while loadingAuth is true; #app-preloader covers the viewport seamlessly!
        return null;
    }

    const renderTool = () => {
        if (!tool || tool === 'home') {
            document.title = "Wellbeing Hub | Professional Supervision";
            return <Home />;
        }

        let ToolComponent = null;

        // Clinical
        if (tool === 'disc') { document.title = "DISC Assessment | Professional Supervision"; ToolComponent = <DiscApp />; }
        else if (tool === 'fivefactor') { document.title = "Five Factor Assessment | Professional Supervision"; ToolComponent = <FiveFactorApp />; }
        else if (tool === 'dass21') { document.title = "DASS-21 Assessment | Professional Supervision"; ToolComponent = <Dass21App />; }
        else if (tool === 'obi16') { document.title = "OBI-16 Assessment | Professional Supervision"; ToolComponent = <Obi16App />; }
        else if (tool === 'uwes17') { document.title = "UWES-17 Assessment | Professional Supervision"; ToolComponent = <Uwes17App />; }
        else if (tool === 'sds6') { document.title = "SDS-6 Assessment | Professional Supervision"; ToolComponent = <Sds6App />; }
        else if (tool === 'copsoq') { document.title = "COPSOQ Work Environment | Professional Supervision"; ToolComponent = <CopsoqTool />; }
        else if (tool === 'archetype') { document.title = "Personality Archetypes | Professional Supervision"; ToolComponent = <ArchetypeApp />; }
        else if (tool === 'sapphire') { document.title = "Psychometric Matrix | Professional Supervision"; ToolComponent = <SapphireApp />; }
        else if (tool === 'workinggenius') { document.title = "Working Genius | Professional Supervision"; ToolComponent = <WorkingGeniusApp />;}
        // Reflective Practice
        else if (tool === 'target') { document.title = "Target Check-in | Professional Supervision"; ToolComponent = <TargetApp />; }
        else if (tool === 'traffic') { document.title = "Traffic Lights | Professional Supervision"; ToolComponent = <TrafficApp />; }
        else if (tool === 'compass') { document.title = "Compass Points | Professional Supervision"; ToolComponent = <CompassApp />; }
        else if (tool === 'cec') { document.title = "Connect, Extend, Challenge | Professional Supervision"; ToolComponent = <CecApp />; }
        else if (tool === 'complexity') { document.title = "Complexity Assessment | Professional Supervision"; ToolComponent = <ComplexityApp />; }
        else if (tool === '3ys') { document.title = "The 3Ys | Professional Supervision"; ToolComponent = <ThreeYsApp />; }
        else if (tool === 'stepinside') { document.title = "Step Inside | Professional Supervision"; ToolComponent = <StepInsideApp />; }
        else if (tool === 'focuscards') { document.title = "Focus Cards | Professional Supervision"; ToolComponent = <FocusCardsApp />; }
        else if (tool === 'morethanwords') { document.title = "More Than Words | Professional Supervision"; ToolComponent = <MoreThanWordsApp />; }
        else if (tool === 'bridge') { document.title = "3-2-1 Bridge | Professional Supervision"; ToolComponent = <BridgeApp />; }
        else if (tool === 'stickingpoints') { document.title = "Sticking Points | Professional Supervision"; ToolComponent = <StickingPointsApp />; }
        else if (tool === 'circleofviewpoints') { document.title = "Circle of Viewpoints | Professional Supervision"; ToolComponent = <CircleOfViewpointsApp />; }
        else if (tool === 'usedtothink') { document.title = "I Used to Think... Now I Think | Professional Supervision"; ToolComponent = <UsedToThinkApp />; }
        else if (tool === 'claimsupport') { document.title = "Claim, Support, Question | Professional Supervision"; ToolComponent = <ClaimSupportApp />; }
        else if (tool === 'tugofwar') { document.title = "Tug-of-War | Professional Supervision"; ToolComponent = <TugOfWarApp />; }
        else if (tool === 'trueforwho') { document.title = "True for Who? | Professional Supervision"; ToolComponent = <TrueForWhoApp />; }
        else if (tool === 'headlines') { document.title = "Headlines | Professional Supervision"; ToolComponent = <HeadlinesApp />; }
        else if (tool === 'generatesort') { document.title = "Generate, Sort, Connect, Elaborate | Professional Supervision"; ToolComponent = <GenerateSortApp />; }
        else if (tool === 'energyaudit') { document.title = "Weekly Energy Audit | Professional Supervision"; ToolComponent = <EnergyAuditApp />; }
        else if (tool === 'fourbatteries') { document.title = "4-Battery Check-In | Professional Supervision"; ToolComponent = <FourBatteriesApp />; }
        else if (tool === 'bandwidthgauge') { document.title = "Workload Bandwidth Gauge | Professional Supervision"; ToolComponent = <BandwidthGaugeApp />; }
        else if (tool === 'connected') { document.title = "Connected Items | Professional Supervision"; ToolComponent = <ConnectedApp />; }
        else if (tool === 'decisioncircle') { document.title = "The Decision Circle | Professional Supervision"; ToolComponent = <DecisionCircleApp />; }

        // Admin
        else if (tool === 'admin-onyx-admin') { document.title = "Onyx Command Center"; ToolComponent = <AdminApp />; }

        // Fallback
        else {
            return <Home />;
        }

        return (
            <Suspense fallback={<ToolLoader />}>
                {ToolComponent}
            </Suspense>
        );
    };

    return (
        <>
            {renderTool()}
            <FloatingProUpgradeButton currentTool={tool} />
            <FloatingToolActionButton currentTool={tool} />
            {triageState === 'open' && <TriageModal isOpen={true} onClose={() => window.location.href = '/?triage=closed'} />}
            {triageState === 'supervision' && <SupervisionTriageModal isOpen={true} onClose={() => window.location.href = '/?triage=closed'} />}
        </>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
}